import { Check, ChevronDown, MoreHorizontal } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { IconButton } from "../../components/IconButton/IconButton";
import { ContextMenu, type ContextMenuItem } from "../../components/Menu/ContextMenu";
import { Tooltip } from "../../components/Tooltip/Tooltip";

export interface MessageActionProps {
  icon: ReactElement<{ size?: number }>;
  /** The tooltip and the button's name ("Copy", "Try again"). */
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

/** One action under a reply: a small ghost icon button with its name as a tooltip. */
export function MessageAction({ icon, label, onClick, disabled }: Readonly<MessageActionProps>) {
  return (
    <Tooltip content={label}>
      <IconButton icon={icon} aria-label={label} variant="ghost" size="sm" onClick={onClick} disabled={disabled} />
    </Tooltip>
  );
}

export interface MessageCopyActionProps {
  icon: ReactElement<{ size?: number }>;
  /** "Copy". */
  label: string;
  /** "Copied", shown for 2 seconds with a check after a copy. */
  copiedLabel: string;
  /** Copies the reply; a rejected promise leaves the button as it was. */
  onCopy: () => Promise<void> | void;
}

/** Copy, which turns into a check with "Copied" for 2 seconds (Figma annotation on "Copy"). */
export function MessageCopyAction({ icon, label, copiedLabel, onCopy }: Readonly<MessageCopyActionProps>) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = async () => {
    try {
      await onCopy();
      setCopied(true);
    } catch {
      // Nothing was copied: the button stays as it was.
    }
  };
  return (
    <>
      <MessageAction
        icon={copied ? <Check /> : icon}
        label={copied ? copiedLabel : label}
        onClick={() => void copy()}
      />
      {/* A focused button's new name isn't reliably announced, so "Copied" is also said here. The region is always
          rendered: screen readers only announce changes to a live region that was already on the page. */}
      <span role="status" className="sr-only">
        {copied ? copiedLabel : ""}
      </span>
    </>
  );
}

export interface MessageMoreActionProps {
  /** "More". */
  label: string;
  /** Figma "More menu": e.g. Copy link to message, Save to knowledge, and Report an issue (danger). */
  items: ContextMenuItem[];
}

/** "…": the reply's less common actions in a menu. */
export function MessageMoreAction({ label, items }: Readonly<MessageMoreActionProps>) {
  return (
    <ContextMenu items={items} size="sm">
      {(openFrom, isOpen) => (
        <Tooltip content={label} disabled={isOpen}>
          <IconButton
            icon={<MoreHorizontal />}
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            variant="ghost"
            size="sm"
            onClick={(event) => openFrom(event.currentTarget)}
          />
        </Tooltip>
      )}
    </ContextMenu>
  );
}

export interface MessageActionsProps {
  /** MessageAction / MessageCopyAction buttons, and a "…" menu. */
  children: ReactNode;
  /** The reply's sources: a quiet trigger after the buttons ("Cited 2 sources") that opens them under the row. */
  sources?: { label: ReactNode; content: ReactNode; defaultOpen?: boolean };
}

/**
 * The row under a finished reply (Figma "Action row"): its actions, then what it cited. The buttons' glyphs line up
 * with the reply's text when the row sits 5px left of it.
 */
export function MessageActions({ children, sources }: Readonly<MessageActionsProps>) {
  const [open, setOpen] = useState(sources?.defaultOpen ?? false);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1">
        {children}
        {sources && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="ml-1 inline-flex cursor-pointer items-center gap-1 rounded [font:var(--type-caption)] text-content-tertiary transition-colors hover:text-content-secondary btn-focus-ring"
          >
            {sources.label}
            <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {sources && open && (
        <div className="mt-2 overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated">
          {sources.content}
        </div>
      )}
    </div>
  );
}
